import {match, P} from "ts-pattern"

export function entries<Obj extends object>(obj: Obj): [keyof Obj, Obj[keyof Obj]][] {
  return Object.entries(obj) as [keyof Obj, Obj[keyof Obj]][]
}

type ObjectFilterMap<Val, Out> = (key: string, value: Val) => [string, Out] | null | undefined

/**
 * This is like someArr.map(mapFn).filter(Boolean) but for objects
 * any `null|undefined` value returned from `mapFn` will be omitted from the final object
 */
export function filterMap<Val, Out>(obj: Record<string, Val>, fn: ObjectFilterMap<Val, Out>): Record<string, Out>
export function filterMap<Val>(obj: Record<string, Val>): <Out>(fn: ObjectFilterMap<Val, Out>) => Record<string, Out>
export function filterMap<Val, Out>(...args: [Record<string, Val>, ObjectFilterMap<Val, Out>] | [obj: Record<string, Val>]) {
  return match(args)
    .with(
      [P._],
      ([obj]) =>
        (fn: ObjectFilterMap<Val, Out>) =>
          filterMapUncurried(obj, fn)
    )
    .with([P._, P._], ([obj, fn]) => filterMapUncurried(obj, fn))
    .exhaustive()
}

const filterMapUncurried = <Val, Out>(obj: Record<string, Val>, fn: ObjectFilterMap<Val, Out>): Record<string, Out> =>
  Object.fromEntries(
    Object.entries(obj)
      .map(([key, value]) => fn(key, value))
      .filter((output): output is [string, NonNullable<Out>] => output !== null && output !== undefined)
  )
